import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import * as React from "react";
import type { PreviewSourceItem } from "../api/client";
import { useKpiCalculationPreview, useKpiRecalculate } from "../api/queries";
import { getKpiLevelLabel } from "../utils/kpi-level-labels";

const SOURCE_LABELS: Record<string, string> = {
  checklist: "ລາຍການກວດສອບ",
  qc: "ການກວດຄຸນນະພາບ",
  internal_audit: "ກວດສອບພາຍໃນ",
  leave: "ລາພັກ",
  overtime: "ລະບຽບເວລາທຳງານ",
  form_submission: "ຟອມສົ່ງຂໍ້ມູນ",
  quotation_invoice: "ໃບສະເໜີລາຄາ/ໃບບິນ",
};


const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => {
  const y = new Date().getFullYear() - 5 + i;
  return { value: String(y), label: String(y) };
});

const PERIOD_TYPE_OPTIONS = [
  { value: "monthly", label: "ຕໍ່ເດືອນ" },
  { value: "quarterly", label: "ຕໍ່ໄຕມາດ" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const m = i + 1;
  return { value: String(m), label: `ເດືອນ ${m}` };
});

const QUARTER_OPTIONS = [1, 2, 3, 4].map((q) => ({
  value: String(q),
  label: `ໄຕມາດ ${q}`,
}));

export function KPICalculationPage() {
  const nav = useNavigate();
  const routerState = useRouterState();
  const isManagementZone =
    routerState.location.pathname.includes("management/okr-kpi");
  const backTo = isManagementZone ? "/app/management/okr-kpi" : "/app/kpis";
  const canUpdate = useActionPermission({
    any: [Permissions.kpi.update, Permissions.mgmt_okr_kpi.update],
  });
  const previewMutation = useKpiCalculationPreview();
  const recalcMutation = useKpiRecalculate();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(currentYear);
  const [periodType, setPeriodType] = React.useState<"monthly" | "quarterly">(
    "monthly",
  );
  const [month, setMonth] = React.useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = React.useState(
    Math.ceil((new Date().getMonth() + 1) / 3),
  );
  const [previewData, setPreviewData] = React.useState<
    PreviewSourceItem[] | null
  >(null);

  const periodParams = React.useMemo(() => {
    const params: { year: number; month?: number; quarter?: number } = { year };
    if (periodType === "monthly") params.month = month;
    else params.quarter = quarter;
    return params;
  }, [year, periodType, month, quarter]);

  const handlePreview = async () => {
    try {
      const result = await previewMutation.mutateAsync(periodParams);
      setPreviewData(result.sources);
    } catch {
      toast.error("ບໍ່ສາມາດໂຫຼດຕົວຢ່າງໄດ້");
    }
  };

  const handleRecalculate = async () => {
    if (!canUpdate) {
      toast.error("ບໍ່ມີສິດອັບເດດ KPI");
      return;
    }
    try {
      const result = await recalcMutation.mutateAsync(periodParams);
      const detail = `ລາຍການກວດສອບ: ${result.checklist}, ການກວດຄຸນນະພາບ: ${result.qc}, ກວດສອບພາຍໃນ: ${result.internal_audit}, ໃບສະເໜີ/ໃບບິນ: ${result.quotation_invoice}`;
      if (result.totalUpdated === 0) {
        toast.warning(
          `ຄຳນວນສຳເລັດແຕ່ອັບເດດ 0 ລາຍການ (${detail}). ກວດສອບ: ບັນທຶກ checklist ຕ້ອງສະຖານະ "ສຳເລັດ" ແລະວັນທີ່ຢູ່ໃນງວດທີ່ເລືອກ; ຕ້ອງມີການມອບຫມາຍ KPI ທີ່ກົງກັບຜູ້ລັນ ຫຼື ຜະແນກ (ແລະ checklist ຕ້ອງມີຜະແນກ ຖ້າ KPI ແມ່ນລະດັບຜະແນກ/ອົງກອນ).`,
        );
      } else {
        toast.success(
          `ຄຳນວນ KPI ສຳເລັດ: ອັບເດດທັງໝົດ ${result.totalUpdated} ລາຍການ (${detail})`,
        );
      }
      setPreviewData(null);
    } catch {
      toast.error("ຄຳນວນ KPI ລົ້ມເຫຼວ");
    }
  };

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="font-semibold text-2xl">ຄຳນວນ KPI ຕາມຊ່ວງເວລາ</h1>
              <p className="mt-1 text-muted-foreground text-sm">
                ເລືອກປີ ແລະ ຊ່ວງເວລາ ເພື່ອເບິ່ງຕົວຢ່າງຫຼືຢືນຢັນຄຳນວນ KPI
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => nav({ to: backTo })}
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບ
            </Button>
          </div>

          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 font-semibold text-lg">ເລືອກຊ່ວງເວລາ</h2>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex w-32 flex-col gap-1.5">
                <label
                  htmlFor="kpi-calc-year"
                  className="block font-medium text-sm"
                >
                  ປີ
                </label>
                <Select
                  value={String(year)}
                  onValueChange={(v) => setYear(Number(v))}
                >
                  <SelectTrigger id="kpi-calc-year" className="w-full">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-36 flex-col gap-1.5">
                <label
                  htmlFor="kpi-calc-period-type"
                  className="block font-medium text-sm"
                >
                  ປະເພດຊ່ວງ
                </label>
                <Select
                  value={periodType}
                  onValueChange={(v) =>
                    setPeriodType(v as "monthly" | "quarterly")
                  }
                >
                  <SelectTrigger id="kpi-calc-period-type" className="w-full">
                    <SelectValue placeholder="ເລືອກປະເພດ" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIOD_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {periodType === "monthly" && (
                <div className="flex w-40 flex-col gap-1.5">
                  <label
                    htmlFor="kpi-calc-month"
                    className="block font-medium text-sm"
                  >
                    ເດືອນ
                  </label>
                  <Select
                    value={String(month)}
                    onValueChange={(v) => setMonth(Number(v))}
                  >
                    <SelectTrigger id="kpi-calc-month" className="w-full">
                      <SelectValue placeholder="ເລືອກເດືອນ" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {periodType === "quarterly" && (
                <div className="flex w-32 flex-col gap-1.5">
                  <label
                    htmlFor="kpi-calc-quarter"
                    className="block font-medium text-sm"
                  >
                    ໄຕມາດ
                  </label>
                  <Select
                    value={String(quarter)}
                    onValueChange={(v) => setQuarter(Number(v))}
                  >
                    <SelectTrigger id="kpi-calc-quarter" className="w-full">
                      <SelectValue placeholder="ເລືອກໄຕມາດ" />
                    </SelectTrigger>
                    <SelectContent>
                      {QUARTER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={previewMutation.isPending}
                onClick={handlePreview}
              >
                {previewMutation.isPending ? "ກຳລັງໂຫຼດ..." : "ເບິ່ງຕົວຢ່າງການຄຳນວນ"}
              </Button>
              <Button
                disabled={
                  recalcMutation.isPending || !canUpdate || !previewData
                }
                onClick={handleRecalculate}
                title={!previewData ? "ກະລຸນາກົດເບິ່ງຕົວຢ່າງການຄຳນວນກ່ອນ" : undefined}
              >
                {recalcMutation.isPending ? "ກຳລັງຄຳນວນ..." : "ຢືນຢັນຄຳນວນ KPI"}
              </Button>
            </div>
          </div>

          {previewData && (
            <div className="rounded-lg border bg-card p-6">
              <h2 className="mb-4 font-semibold text-lg">
                ສະຫຼຸບແຫຼ່ງຂໍ້ມູນທີ່ນຳມາຄຳນວນ
              </h2>
              <div className="space-y-3">
                {previewData.map((src) => (
                  <div key={src.source} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {SOURCE_LABELS[src.source] ?? src.source}
                      </span>
                      {src.connected ? (
                        <Badge variant="default">ເຊື່ອມຕໍ່</Badge>
                      ) : (
                        <Badge variant="secondary">
                          {src.message ?? "ຍັງບໍ່ເຊື່ອມຕໍ່"}
                        </Badge>
                      )}
                    </div>
                    {src.connected && (
                      <>
                        <p className="mt-1 text-muted-foreground text-sm">
                          ຈຳນວນຂໍ້ມູນໃນຊ່ວງ: {src.recordCount}
                        </p>
                        {src.message && (
                          <p className="mt-2 text-amber-700 text-sm dark:text-amber-500">
                            {src.message}
                          </p>
                        )}
                        {src.kpis.length > 0 && (
                          <div className="mt-2">
                            <p className="font-medium text-sm">
                              KPI ທີ່ຈະຖືກອັບເດດ:
                            </p>
                            <ul className="mt-1 list-inside list-disc text-muted-foreground text-sm">
                              {src.kpis.map((k) => (
                                <li key={k.id}>
                                  {k.name} ({getKpiLevelLabel(k.level)})
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
